import React, {useCallback, useMemo, useState} from 'react';
import type {SxProps} from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {ThemeProvider} from '@mui/system';
import {useNavigate} from 'react-router';
import type {DDSStatus, Product} from 'services/metadata/types';
import {Configuration} from 'configuration';
import {useLazyNotes} from 'services/data';
import {
  countProductsData,
  useDeleteManifestationMutation,
  useGetManifestationEditValue,
  useLazyManifestationMediaData,
  useManifestationEditValue,
  useSetManifestationEditValue,
} from 'services/metadata';
import {useShortcut} from 'services/shortcuts';
import {useLayoutTheme} from 'components/scene/hooks/useLayoutTheme';
import {DataForm} from 'schema/form';
import {useManifestationCount} from 'scenes/updateMetadata/hooks';
import type {
  ManifestationCardProps,
  ManifestationPanel as ManifestationPanelType,
} from './types';
import {useExternalSuggestionsContext} from '../../contexts/ExternalSuggestionsContext';
import {ChangeRequestCard} from '../ChangeRequestCard';
import {
  ManifestationButtons,
  ManifestationDescription,
  ManifestationHeader,
  ManifestationPanel,
} from './components';
import type {ManifestationEditState} from './hooks';
import {
  useHandleManifestationStatus,
  useManifestationDescription,
  useManifestationFormConfiguration,
} from './hooks';

const manifestationButtonWrapperSx: SxProps = {
  zIndex: 999,
};

type Props = {
  props: ManifestationCardProps;
  editState: ManifestationEditState;
};

export const ManifestationCardCore: React.FC<Props> = ({props, editState}) => {
  const getEditValue = useGetManifestationEditValue();
  const setEditValue = useSetManifestationEditValue(props.manifestationId);
  const {
    manifestationStatus,
    relatedScope,
    schema,
    readonly,
    showErrors,
    valid,
  } = editState;

  const {handleManifestationStatusPreview, handleManifestationStatusChanged} =
    useHandleManifestationStatus(props.manifestationId);

  const manifestationFormConfiguration = useManifestationFormConfiguration();

  const [isExpanded, setIsExpanded] = useState(!!props.initialExpanded);

  useShortcut('expandAllManifestations', () => setIsExpanded(true));
  useShortcut('collapseAllManifestations', () => setIsExpanded(false));

  const ean = getEditValue('ean', props.manifestationId);
  const eanAsString = typeof ean === 'string' ? ean : undefined;
  const eans = eanAsString ? [eanAsString] : [];

  const {media, refreshMedia} = useLazyManifestationMediaData(
    eans,
    eans.length > 0,
    false, // props.mock,
  );

  const trykkunderlagReservedEditValue = getEditValue(
    'trykkunderlagReserved',
    props.manifestationId,
  );
  const trykkunderlagReserved = trykkunderlagReservedEditValue === true;

  const {notes, createNote, updateNote, removeNote} = useLazyNotes(
    props.manifestationId,
    true,
    'manifestation',
    false, // props.mock,
  );

  const {getWorkSuggestions} = useExternalSuggestionsContext();
  const workSuggestions = getWorkSuggestions();

  const [showManifestationPanel, setShowManifestationPanel] = useState<
    ManifestationPanelType | undefined
  >();

  const handleToggleManifestationPanel = useCallback(
    (panel: ManifestationPanelType) => {
      setShowManifestationPanel(prev => (prev === panel ? undefined : panel));
    },
    [],
  );

  const [deleteManifestationMutation] = useDeleteManifestationMutation();
  const count = useManifestationCount();
  const navigate = useNavigate();
  const onManifestationDelete = useCallback(
    () =>
      deleteManifestationMutation({
        manifestationId: props.manifestationId,
        workId: props.workId,
      }).then(() => {
        if (count < 2) {
          navigate('/overview');
        } else {
          location.reload();
        }
      }),
    [
      count,
      deleteManifestationMutation,
      navigate,
      props.manifestationId,
      props.workId,
    ],
  );

  const {metaqUrl, historyUrl} = useMemo(() => {
    return {
      metaqUrl: `${Configuration.bb_app.search}/work/${props.workId}?ean=${ean}`,
      historyUrl: `${Configuration.bb_app.history}?ean=${ean}`,
    };
  }, [ean, props.workId]);

  const changeRequestSchemaSize = 12;
  const manifestationSchemaSize = showManifestationPanel ? 7 : 9;
  const manifestationButtonsSize = 12 - manifestationSchemaSize;
  const manifestationSchemaLayout = useLayoutTheme(manifestationSchemaSize);
  const manifestationButtonsLayout = useLayoutTheme(manifestationButtonsSize);

  const miniMode = !!showManifestationPanel;
  const notesCount = notes.data?.length ?? 0;
  const mediaCount = (media.data ?? []).filter(m => !m.compressed)?.length ?? 0;
  const infoCount = useMemo(() => {
    const ddsStatus = getEditValue('ddsStatus', props.manifestationId) as
      | DDSStatus[]
      | undefined;
    const products = getEditValue('products', props.manifestationId) as
      | Product[]
      | undefined;

    const workSuggestionsCount = Object.keys(workSuggestions ?? {}).length;

    return (
      (getEditValue('availabilityStatus', props.manifestationId) ? 1 : 0) +
      (ddsStatus?.length ?? 0) +
      workSuggestionsCount +
      (countProductsData(products) ?? 0) +
      (getEditValue('registered', props.manifestationId) ? 1 : 0) +
      (getEditValue('completedDate', props.manifestationId) ? 1 : 0)
    );
  }, [workSuggestions, getEditValue, props.manifestationId]);

  const manifestationDescription = useManifestationDescription(
    getEditValue,
    props.manifestationId,
  );

  return (
    <>
      <Accordion
        defaultExpanded={props.initialExpanded}
        slotProps={{transition: {unmountOnExit: true}}}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        expanded={isExpanded}>
        <AccordionSummary>
          <ManifestationHeader
            manifestationId={props.manifestationId}
            isExpanded={isExpanded}
            showErrors={showErrors}
            valid={valid}
            manifestationStatus={manifestationStatus}
            notesCount={notesCount}
            mediaCount={mediaCount}
          />
        </AccordionSummary>
        <AccordionDetails>
          <Box
            component={Grid}
            container
            spacing={2}
            padding={0}
            flex={1}
            flexDirection={'row'}>
            {props.changeRequests?.map(mcr => (
              <Box
                key={mcr.taskId}
                component={Grid}
                size={{xs: changeRequestSchemaSize}}>
                <ChangeRequestCard
                  key={mcr.taskId}
                  changeRequestId={mcr.taskId}
                />
              </Box>
            ))}
            <Box component={Grid} size={{xs: manifestationSchemaSize}}>
              <ThemeProvider theme={manifestationSchemaLayout}>
                <DataForm
                  id={props.manifestationId}
                  schema={schema}
                  configuration={manifestationFormConfiguration}
                  relatedData={relatedScope}
                  mode={readonly ? 'read-only' : 'edit'}
                  showErrors={showErrors}
                  getEditValue={getEditValue}
                  useEditValue={useManifestationEditValue}
                  setEditValue={setEditValue}
                />
                <ManifestationDescription
                  description={manifestationDescription}
                />
              </ThemeProvider>
            </Box>
            <Box component={Grid} size={{xs: manifestationButtonsSize}}>
              <ThemeProvider theme={manifestationButtonsLayout}>
                <Box component={Grid} container spacing={0}>
                  {showManifestationPanel ? (
                    <Box component={Grid} size={{xs: 10}}>
                      <ManifestationPanel
                        manifestationId={props.manifestationId}
                        showManifestationPanel={showManifestationPanel}
                        onToggleManifestationPanel={
                          handleToggleManifestationPanel
                        }
                        media={media}
                        refreshMedia={refreshMedia}
                        notes={notes}
                        createNote={createNote}
                        updateNote={updateNote}
                        removeNote={removeNote}
                        workSuggestions={workSuggestions}
                        trykkunderlagReserved={trykkunderlagReserved}
                      />
                    </Box>
                  ) : null}

                  <Box
                    component={Grid}
                    size={{xs: showManifestationPanel ? 2 : 12}}
                    sx={manifestationButtonWrapperSx}>
                    <ManifestationButtons
                      manifestationId={props.manifestationId}
                      manifestationStatus={manifestationStatus}
                      notesCount={notesCount}
                      mediaCount={mediaCount}
                      infoCount={infoCount}
                      miniMode={miniMode}
                      panel={showManifestationPanel}
                      changeStatusDisabled={props.changeStatusDisabled}
                      moveDisabled={props.moveDisabled}
                      onManifestationStatusPreview={
                        handleManifestationStatusPreview
                      }
                      onManifestationStatusChanged={
                        handleManifestationStatusChanged
                      }
                      onToggleManifestationPanel={
                        handleToggleManifestationPanel
                      }
                      metaqUrl={metaqUrl}
                      historyUrl={historyUrl}
                      ean={eanAsString}
                      onManifestationDelete={onManifestationDelete}
                      deleteDisabled={props.deleteDisabled}
                      trykkunderlagReserved={trykkunderlagReserved}
                    />
                  </Box>
                </Box>
              </ThemeProvider>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
